import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDollarSign,
  Globe,
  ImagePlus,
  Lock,
  Rocket,
  Sparkles,
  Unlock,
} from 'lucide-react';
import { Modal, Button, Input } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { toast } from '@/components/feedback/toast';
import { cn, slugify } from '@/lib/utils';
import { PLATFORM, ROUTES } from '@/config/constants';
import { CATEGORIES } from '../categories';
import { useCreateCommunity } from '../hooks/useCommunities';
import { uploadCommunityBanner } from '../api/banners';

interface CreateCommunityModalProps {
  open: boolean;
  onClose: () => void;
}

type Mode = 'free' | 'paid' | 'private';

/** Dark glass, same family as the Locker / toolkit menu so it feels native — a
 *  single soft electric-blue glow at the top for life (on-brand, no violet),
 *  inset highlight + deep shadow for depth. Translucent: low alphas + blur. */
const SHEET = cn(
  'max-w-lg overflow-hidden rounded-2xl border border-[#dbeafe] bg-white text-[#0f172a]',
  'shadow-[0_30px_90px_rgba(2,6,23,0.28),inset_0_1px_0_rgba(255,255,255,0.9)]',
  '[--text-hi:#0f172a] [--text-mid:#475569] [--text-lo:#64748b] [--edge:rgba(37,99,235,0.16)]',
);
const WHITE_BUTTON =
  'bg-[#0a84ff] text-white shadow-[0_14px_30px_rgba(10,132,255,0.24)] hover:-translate-y-px hover:bg-[#0072e5]';
const SOFT_FIELD =
  'border-[#bfdbfe] bg-[#f8fbff] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#0a84ff] focus:bg-white';

const STEPS = ['Basics', 'Access', 'Review'];
const ACCENTS = ['#54b9fe', '#a855f7', '#22c55e', '#f97316', '#ef4444', '#fbbf24'];
const FEE_PCT = PLATFORM.feeBps / 100;

/**
 * Create-a-community wizard. Three light steps — Basics (name, tagline,
 * category) → Access (free / paid / private) → Review (preview, accent, banner)
 * → launch. Kept short on purpose. Writes through `useCreateCommunity` so
 * Explore refreshes automatically, then routes to the new community.
 */
export function CreateCommunityModal({ open, onClose }: CreateCommunityModalProps) {
  const { user } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const navigate = useNavigate();
  const create = useCreateCommunity();

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('free');
  const [price, setPrice] = useState('19');
  const [accent, setAccent] = useState(ACCENTS[0]);
  const [banner, setBanner] = useState<string | null>(null);

  // Fresh slate every time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setName('');
    setTagline('');
    setCategory(null);
    setMode('free');
    setPrice('19');
    setAccent(ACCENTS[0]);
    setBanner(null);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!user) {
    return (
      <Modal open={open} onClose={onClose} className={SHEET}>
        <div className="flex flex-col items-center text-center">
          <span className="mb-4 grid h-11 w-11 place-items-center rounded-full bg-[#e0f2fe] text-[#0a84ff]">
            <Rocket size={20} strokeWidth={2} />
          </span>
          <h2 className="text-lg font-extrabold text-hi">Sign in to create a community</h2>
          <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-mid">
            You&apos;ll need a WINR account before launching your own space.
          </p>
        </div>
        <Button
          className={cn('mt-6 w-full', WHITE_BUTTON)}
          onClick={() => {
            onClose();
            openAuth();
          }}
        >
          Log in to continue
        </Button>
      </Modal>
    );
  }

  const slug = slugify(name) || 'your-community';
  const nameValid = name.trim().length >= 2;
  const paid = mode === 'paid';
  const priceCents = Math.round((parseFloat(price) || 0) * 100);
  const accessValid = !paid || priceCents > 0;
  const canContinue = step === 0 ? nameValid : step === 1 ? accessValid : true;
  const categoryName = CATEGORIES.find((c) => c.id === category)?.name;

  function onBanner(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    // Optimized upload to storage — data URLs in `communities.banner` are banned.
    void uploadCommunityBanner(file, user.id)
      .then(setBanner)
      .catch((err: Error) => toast(err.message || 'Banner upload failed', 'error'));
  }

  async function launch() {
    if (!user) return;
    try {
      const community = await create.mutateAsync({
        name: name.trim(),
        tagline: tagline.trim() || undefined,
        category,
        access: paid ? 'paid' : 'free',
        privacy: mode === 'private' ? 'private' : 'public',
        priceCents: paid ? priceCents : 0,
        color: accent,
        banner,
        ownerId: user.id,
      });
      onClose();
      navigate(ROUTES.community(community.id));
    } catch {
      /* surfaced via create.isError below */
    }
  }

  const next = () => (step < 2 ? setStep((s) => s + 1) : void launch());

  return (
    <Modal open={open} onClose={onClose} className={SHEET}>
      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-hi">Create your community</h2>
        <p className="mt-0.5 text-sm text-mid">Set it up in three quick steps.</p>
      </div>

      {/* Progress */}
      <div className="mb-6 flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-1 flex-col gap-1.5">
            <div
              className={cn(
                'h-1 rounded-pill transition-colors duration-300',
                i <= step ? 'bg-[#0a84ff]' : 'bg-[#dbeafe]',
              )}
            />
            <span
              className={cn(
                'text-[10px] font-bold uppercase tracking-[0.4px]',
                i === step ? 'text-hi' : 'text-lo',
              )}
            >
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Step 1 — Basics */}
      {step === 0 && (
        <div className="flex animate-fade-in flex-col gap-4">
          <Field label="Community name">
            <Input
              autoFocus
              maxLength={40}
              placeholder="e.g. Beatmakers Guild"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={SOFT_FIELD}
            />
            {nameValid && (
              <p className="mt-1.5 text-[11px] text-lo">
                joinwinr.com/c/<span className="text-mid">{slug}</span>
              </p>
            )}
          </Field>

          <Field label="Tagline" hint="One line — what members get">
            <Input
              maxLength={80}
              placeholder="Where producers swap beats & land placements"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              className={SOFT_FIELD}
            />
          </Field>

          <Field label="Category" hint="optional">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const selected = category === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(selected ? null : c.id)}
                    aria-pressed={selected}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-semibold transition-all duration-150 active:scale-[0.96] [&_svg]:h-4 [&_svg]:w-4',
                      selected
                        ? 'border-[#0a84ff] bg-[#e0f2fe] text-[#075985]'
                        : 'border-[#dbeafe] bg-white text-mid hover:border-[#93c5fd] hover:bg-[#f8fbff] hover:text-hi',
                    )}
                  >
                    {c.icon}
                    {c.name}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      )}

      {/* Step 2 — Access */}
      {step === 1 && (
        <div className="flex animate-fade-in flex-col gap-4">
          <div className="grid grid-cols-3 gap-2.5">
            <AccessCard active={mode === 'free'} onClick={() => setMode('free')} icon={Unlock} title="Free" desc="Anyone can join" />
            <AccessCard active={mode === 'paid'} onClick={() => setMode('paid')} icon={CircleDollarSign} title="Paid" desc="Monthly fee" />
            <AccessCard active={mode === 'private'} onClick={() => setMode('private')} icon={Lock} title="Private" desc="Invite only" />
          </div>

          {paid && (
            <Field label="Price / month">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-mid">$</span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  className={cn('pl-7', SOFT_FIELD)}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
              <p className="mt-1.5 text-[11px] text-lo">
                You keep {100 - FEE_PCT}% · WINR fee {FEE_PCT}%
              </p>
            </Field>
          )}

          {mode === 'private' && (
            <p className="flex items-start gap-2 rounded-md border border-[#dbeafe] bg-[#f8fbff] px-4 py-3 text-[12px] leading-relaxed text-mid">
              <Lock size={14} className="mt-0.5 flex-none text-lo" />
              Hidden from Explore. Members join with an invite link you share.
            </p>
          )}
        </div>
      )}

      {/* Step 3 — Review */}
      {step === 2 && (
        <div className="flex animate-fade-in flex-col gap-4">
          <div className="overflow-hidden rounded-xl border border-[#dbeafe] bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
            <div
              className="h-20 bg-cover bg-center"
              style={
                banner
                  ? { backgroundImage: `url(${banner})` }
                  : { background: `linear-gradient(135deg, ${accent}, transparent)` }
              }
            />
            <div className="flex flex-col gap-2 p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="truncate text-lg font-extrabold text-hi">
                  {name.trim() || 'Your community'}
                </h3>
                <span
                  className={cn(
                    'flex-none rounded-pill px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.4px]',
                    paid ? 'bg-[#e0f2fe] text-[#075985]' : 'text-[#0a84ff]',
                  )}
                >
                  {paid ? `$${price || '0'}/mo` : 'Free'}
                </span>
              </div>
              {tagline.trim() && (
                <p className="text-[13px] leading-relaxed text-[#475569]">{tagline.trim()}</p>
              )}
              <div className="mt-1 flex items-center gap-2 text-[11px] text-mid">
                {categoryName && (
                  <span className="rounded-pill border border-[#dbeafe] bg-[#f8fbff] px-2 py-0.5">{categoryName}</span>
                )}
                <span className="inline-flex items-center gap-1">
                  {mode === 'private' ? <Lock size={12} /> : <Globe size={12} />}
                  {mode === 'private' ? 'Invite only' : 'Public'}
                </span>
              </div>
            </div>
          </div>

          <Field label="Accent">
            <div className="flex gap-2">
              {ACCENTS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Accent ${c}`}
                  onClick={() => setAccent(c)}
                  className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 active:scale-95"
                  style={{ backgroundColor: c, borderColor: accent === c ? '#fff' : 'transparent' }}
                />
              ))}
            </div>
          </Field>

          <Field label="Banner" hint="optional">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-[#93c5fd] bg-[#f8fbff] px-4 py-3 text-[12px] font-semibold text-mid transition-colors hover:border-[#0a84ff] hover:bg-[#e0f2fe] hover:text-hi">
              <input type="file" accept="image/*" className="sr-only" onChange={onBanner} />
              <ImagePlus size={15} />
              {banner ? 'Change banner' : 'Upload a banner'}
            </label>
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-lo">
              <Sparkles size={11} className="text-accent" />
              <span className="font-bold text-accent">WINR tip</span> · 1600 × 600px looks sharpest.
            </p>
          </Field>

          {create.isError && (
            <p className="text-sm font-medium text-danger">
              {(create.error as Error)?.message ?? 'Could not create community. Try again.'}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-7 flex items-center justify-between gap-3">
        {step === 0 ? (
          <Button variant="ghost" className="active:scale-95" onClick={onClose}>
            Cancel
          </Button>
        ) : (
          <Button variant="ghost" className="active:scale-95" onClick={() => setStep((s) => s - 1)}>
            <ArrowLeft size={16} /> Back
          </Button>
        )}

        <Button className={cn('active:scale-95', WHITE_BUTTON)} onClick={next} disabled={!canContinue || create.isPending}>
          {step < 2 ? (
            <>
              Continue <ArrowRight size={16} />
            </>
          ) : create.isPending ? (
            'Launching…'
          ) : (
            <>
              <Rocket size={16} /> Launch
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-[11px] font-bold uppercase tracking-[0.4px] text-lo">{label}</label>
        {hint && <span className="text-[10px] text-lo">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function AccessCard({
  active,
  onClick,
  icon: Icon,
  title,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Unlock;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'relative flex flex-col items-center gap-2 rounded-xl border px-2 py-4 text-center transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.97]',
        active
          ? 'border-[#0a84ff] bg-[#e0f2fe] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]'
          : 'border-[#dbeafe] bg-white hover:border-[#93c5fd] hover:bg-[#f8fbff]',
      )}
    >
      {active && (
        <span className="absolute right-2 top-2 grid h-4 w-4 place-items-center rounded-full bg-accent text-black">
          <Check size={10} strokeWidth={3} />
        </span>
      )}
      <Icon size={20} strokeWidth={1.9} className={active ? 'text-[#0a84ff]' : 'text-mid'} />
      <div>
        <div className="text-[13px] font-bold text-hi">{title}</div>
        <div className="mt-0.5 text-[10.5px] leading-snug text-mid">{desc}</div>
      </div>
    </button>
  );
}
